import React, { useLayoutEffect } from 'react';
import {
    matchRoutes, RouteObject, useLocation, useParams,
} from 'react-router-dom';

import { useGetDocumentByIdAdminLazy, useGetChapterByIdAdminLazy, useGetSectionByIdAdminLazy } from '@SERVICES';

import Subheader from '@COMPONENTS/HEADERS/Subheader';
import ROUTES from '@CONSTANTS/ROUTES.constant';

const routes: RouteObject[] = [
    { path: ROUTES.admin.documents.path },
    { path: ROUTES.admin.documents.id.path },
    { path: ROUTES.admin.chapters.id.path },
    { path: ROUTES.admin.sections.id.path },
    { path: ROUTES.admin.manageVendors.path },
    { path: ROUTES.admin.logs.path },
    { path: ROUTES.admin.manageUsersPermissions.path },
    { path: ROUTES.admin.appSettings.path },
    { path: ROUTES.admin.systemSettings.path },
];

function AdminSubheader() {
    const [getDocument, { data: documentData, isFetching: isDocumentFetching }] = useGetDocumentByIdAdminLazy();
    const [getChapter, { data: chapterData, isFetching: isChapterFetching }] = useGetChapterByIdAdminLazy();
    const [getSection, { data: sectionData, isFetching: isSectionFetching }] = useGetSectionByIdAdminLazy();

    const location = useLocation();
    const params = useParams();

    const [{ route }] = matchRoutes(routes, location) || [{ route: { path: null } }];

    const isDocumentPage = route.path === ROUTES.admin.documents.id.path;
    const isChapterPage = route.path === ROUTES.admin.chapters.id.path;
    const isSectionPage = route.path === ROUTES.admin.sections.id.path;
    const isVendorsPage = route.path === ROUTES.admin.manageVendors.path;
    const isLogsPage = route.path === ROUTES.admin.logs.path;
    const isUserPermissionsPage = route.path === ROUTES.admin.manageUsersPermissions.path;
    const isAppSettingsPage = route.path === ROUTES.admin.appSettings.path;
    const isSystemSettingsPage = route.path === ROUTES.admin.systemSettings.path;

    useLayoutEffect(() => {
        const { id } = params;

        if (isDocumentPage) {
            // Skip fetching document data here - the Document page handles it
            // getDocument(+id!, true);
        } else if (isChapterPage) {
            // Skip fetching chapter data here - the EditChapter page handles it
            // getChapter(+id!, true);
        } else if (isSectionPage) {
            getSection(+id!, true);
        }
    }, [
        isDocumentPage,
        isChapterPage,
        isSectionPage,
        getDocument,
        getChapter,
        getSection,
        params,
    ]);

    function getTitle() {
        if (isVendorsPage) {
            return 'Manage Vendors';
        }

        if (isLogsPage) {
            return 'Manage Logs';
        }

        if (isUserPermissionsPage) {
            return 'Manage Users';
        }

        if (isAppSettingsPage) {
            return 'App & Document Settings';
        }

        if (isSystemSettingsPage) {
            return 'System Settings';
        }

        const main = 'Manage Document Templates';

        if (isDocumentFetching || isChapterFetching || isSectionFetching) {
            return main;
        }

        const documentName = documentData?.data?.name;
        const chapterName = chapterData?.data?.name;
        const sectionName = sectionData?.data?.name;

        if (isDocumentPage) {
            if (documentName) {
                return [
                    main,
                    ' %s Document - ',
                    documentName,
                ].join(' ');
            }
        } else if (isChapterPage) {
            if (chapterName) {
                return [
                    main,
                    ' %s Document - ',
                    chapterData.data.document.name!,
                    ' %s Chapter - ',
                    chapterName,
                ].join(' ');
            }
        } else if (isSectionPage) {
            if (sectionName) {
                return [
                    main,
                    ' %s Document - ',
                    sectionData.data.document.name!,
                    ' %s Chapter - ',
                    sectionData.data.chapter.name!,
                    ' %s Section - ',
                    sectionName,
                ].join(' ');
            }
        }

        return main;
    }

    const title = getTitle();

    return <Subheader title={title} />;
}

export default AdminSubheader;
